/*
 * ******************************************************************
 * Copyright (c) 2022-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Component, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChildren } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface, ClrDateContainer } from "@clr/angular";
import { Observable, Subject } from "rxjs";

// @formatter:off
@Component({
    standalone: false,
    selector: 'dg-date-filter',
    templateUrl: './datagrid-date-filter.component.html',
    styleUrls: ['./datagrid-date-filter.component.scss']
})
// @formatter:on
export class DatagridDateFilter<T> implements ClrDatagridFilterInterface<T>, OnDestroy {

    /**
     * Name of the property used for filtering.
     */
    @Input()
    public propertyName: string;

    @Input()
    public startDateFilterProperty: string;

    @Input()
    public endDateFilterProperty: string;

    @Input()
    public set clrFilterValue(value: string[]) {
        this.presetFilter(value);
    }

    // The filter values are formatted in the YYYY-MM-DD format.
    @Input()
    public dateOnly: boolean = false;

    public get clrFilterValue(): string[] {
        return [
            this.formatDate(this.startDate),
            this.formatDate(this.endDate)
        ];
    }

    @Output()
    clrFilterValueChange: EventEmitter<any> = new EventEmitter();

    private changesSubject: Subject<T> = new Subject<T>();
    private destroySubject: Subject<void> = new Subject<void>();

    public startDate: Date;
    public endDate: Date;

    @ViewChildren(ClrDateContainer)
    private dateContainerQuery: QueryList<ClrDateContainer>;

    constructor(protected filterContainer: ClrDatagridFilter) {
        filterContainer.setFilter(this);
    }

    onStartDateChange(date: Date): void {
        this.startDate = date ? new Date(date) : date;
        this.clrFilterValueChange.emit(this.clrFilterValue);
        this.changesSubject.next(null);
    }

    onEndDateChange(date: Date): void {
        this.endDate = date ? new Date(date) : date;
        this.clrFilterValueChange.emit(this.clrFilterValue);
        this.changesSubject.next(null);
    }

    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return (this.startDate != null || this.endDate != null);
    }

    /**
     * Tests if an item matches a search text
     */
    public accepts(item: T): boolean {
        const property = new Date(this.getProperty(item));

        if (this.startDate && this.endDate) {
            return (property >= this.startDate) && (property <= this.endDate);
        } else if (this.startDate) {
            return (property >= this.startDate);
        } else if (this.endDate) {
            return (property <= this.endDate);
        }

        return false;
    }

    /**
     * The Observable required as part of the Filter interface
     */
    public get changes(): Observable<T> {
        return this.changesSubject.asObservable();
    }

    private getProperty(item: T): Date {
        const props: string[] = this.propertyName.split(".");
        let result: any = item;
        for (const prop of props) {
            result = result[prop];
        }
        return result;
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public get state(): any {
        if (this.startDate && this.endDate) {
            return [
                {
                    property: this.startDateFilterProperty,
                    value: this.formatDate(this.startDate)
                },
                {
                    property: this.endDateFilterProperty,
                    value: this.formatDate(this.endDate)
                }
            ]
        }
        if (this.startDate) {
            return {
                property: this.startDateFilterProperty,
                value: this.formatDate(this.startDate)
            }
        }
        if (this.endDate) {
            return {
                property: this.endDateFilterProperty,
                value: this.formatDate(this.endDate)
            }
        }
    }

    private presetFilter(values: string[]) {
        if (values && Array.isArray(values)) {
            this.startDate = values[0] ? new Date(values[0]) : null;
            this.endDate = values[1] ? new Date(values[1]) : null;
        }
    }

    private formatDate(date: Date): string {
        if (this.dateOnly) {
            return date ? this.formatDateOnly(date) : null;
        }
        return date ? new Date(date).toISOString() : null;
    }

    // Format the date as YYYY-MM-DD
    private formatDateOnly(date: Date) {
        const yyyy = date.getFullYear().toString();
        const mm = (date.getMonth()+1).toString(); // getMonth() is zero-based
        const dd  = date.getDate().toString();

        return yyyy + '-' + (mm[1] ? mm : "0" + mm[0]) + '-' + (dd[1] ? dd :"0" + dd[0]);
    }
}
